import { LambdaSecurityService } from './services/lambda-security/lambda-security.service';
import { BrowserModule, Meta } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { MetaReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '@env/environment';
import { environment as env } from '@env/environment';
import { appReducers } from './app.reducer';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AngularMaterialModule } from './material.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { LazyLoadImageModule } from 'ng-lazyload-image';
/*----- Commons -----*/
import { HeaderModule } from './commons/header/header.module';
import { FooterComponent } from './commons/footer/footer.component';
import { ShoppingDialogComponent } from './commons/shopping-dialog/shopping-dialog.component';
import { ContactDataStickyBarComponent } from './commons/contact-data-sticky-bar/contact-data-sticky-bar.component';
/*----- Pages -----*/
import { ProductsProfileComponent } from './products/products-profile/products-profile.component';
import { ProductsBaseComponent } from './products/products-base/products-base.component';
import { ProductsIdealComponent } from './products/products-ideal/products-ideal.component';
import { ProductsFeedbackComponent } from './products/products-feedback/products-feedback.component';
import { ProductsUserZoneComponent } from './products/products-user-zone/products-user-zone.component';
import { ProductsPaymentComponent } from './products/products-payment/products-payment.component';
/*----- Pages Components -----*/
import { BaseCardComponent } from './products/components/base-card/base-card.component';
import { ProductCardComponent } from './products/components/product-card/product-card.component';
import { ResumeComponent } from './products/components/resume/resume.component';
import { CarouselCardComponent } from './products/components/carousel-card/carousel-card.component';
import { ExpansionListComponent } from './products/components/expansion-list/expansion-list.component';
import { PublicationCardComponent } from './products/components/publication-card/publication-card.component';
import { DetailModalComponent } from './products/components/detail-modal/detail-modal.component';
import { ProductsCheckoutComponent } from './products/products-checkout/products-checkout.component';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ScullyLibModule } from '@scullyio/ng-lib';
import { storageMetaReducer } from './redux/storage.metareducer';
import { LoginComponent } from './commons/login-component/login-component.component';
import { MyProductsComponent } from './products/products-user-zone/my-products/my-products.component';
import { ShoppingBarComponent } from './products/components/shopping-bar/shopping-bar.component';
import { FilterModalComponent } from './products/components/filter-modal/filter-modal.component';
import { AppointmentModalComponent } from './products/components/appointment-modal/appointment-modal.component';
import { WarningMessageModalComponent } from './products/components/warning-message-modal/warning-message-modal.component';
import { CardOffersComponent } from './products/components/publication-card/card-offers/card-offers.component';
import { CardProfileComponent } from './products/components/publication-card/card-profile/card-profile.component';
import { ConfirmTelephoneComponent } from './products/components/confirm-telephone/confirm-telephone.component';
import { ContactenosComponent } from './modals/contactenos/contactenos.component';
import { SupportLineComponent } from './commons/support-line/support-line.component';

import { RecaptchaModule } from 'ng-recaptcha';
import { ToastrModule } from 'ngx-toastr';
import { ConfirmPublicationComponent } from './products/components/confirm-publication/confirm-publication.component';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import {
  CcAuthModule,
  CcFooterModule,
  CcHeaderModule,
} from 'cc-components-lib';
import { SERVICES_CONFIG } from 'cc-components-lib/src/lib/cc-services';

/*----- Utilities -----*/
import { getEnvironmentName } from './utils/getEnvironmentName/getEnvironmentName';

export const metaReducers: MetaReducer<any>[] = [storageMetaReducer];

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    ProductsProfileComponent,
    ProductsBaseComponent,
    BaseCardComponent,
    ResumeComponent,
    ShoppingDialogComponent,
    ProductsIdealComponent,
    CarouselCardComponent,
    ProductsFeedbackComponent,
    ProductsUserZoneComponent,
    ExpansionListComponent,
    PublicationCardComponent,
    ProductCardComponent,
    DetailModalComponent,
    ProductsCheckoutComponent,
    LoginComponent,
    MyProductsComponent,
    ShoppingBarComponent,
    FilterModalComponent,
    AppointmentModalComponent,
    WarningMessageModalComponent,
    CardOffersComponent,
    CardProfileComponent,
    ConfirmTelephoneComponent,
    ContactenosComponent,
    ConfirmPublicationComponent,
    SupportLineComponent,
    ContactDataStickyBarComponent,
  ],
  imports: [
    CcAuthModule.forRoot({
      environmentName: getEnvironmentName(env),
      name: getEnvironmentName(env),
      dataDogAppId: environment.dataDogAppId,
      dataDogClientToken: environment.dataDogClientToken,
      dataDogService: environment.dataDogService,
      apiTokenRecaptcha: environment.apiUrlEmail,
      RE_CAPTCHA_KEY: environment.RE_CAPTCHA_KEY,
      formApi: environment.lambdaApiUrl + '/',
      apiUrlTokenCognito: environment.apiUrlTokenCognito,
      userCognito: environment.userCognito,
      passCognito: environment.passCognito,
      firebaseConfig: environment.firebaseConfig,
      apiLambdasUrl: environment.lambdaApiUrl,
      appUrl: environment.environmenturl,
      secretIv: environment.secretIv,
      secretKey: environment.secretKey,
      urlPreferredChannels: environment.urlPreferredChannels,
    }),
    CommonModule,
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    AppRoutingModule,
    AngularMaterialModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    CarouselModule,
    CcHeaderModule,
    HeaderModule,
    CcFooterModule.forRoot({
      name: getEnvironmentName(env),
      dataDogAppId: environment.dataDogAppId,
      dataDogClientToken: environment.dataDogClientToken,
      dataDogService: environment.dataDogService,
      environmenturl:
        environment.environmenturl[environment.environmenturl.length - 1] ===
        '/'
          ? environment.environmenturl.substring(
              0,
              environment.environmenturl.length - 1,
            )
          : environment.environmenturl,
      urlPreferredChannels: environment.urlPreferredChannels,
    }),
    StoreModule.forRoot(appReducers, { metaReducers }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    ScullyLibModule,
    RecaptchaModule,
    ToastrModule.forRoot(),
    GoogleTagManagerModule.forRoot({
      id: environment.google_tag_manager_key,
    }),
    LazyLoadImageModule,
  ],
  providers: [
    Meta,
    {
      provide: SERVICES_CONFIG,
      useValue: {
        urlCustomerValidation: '',
        secretIv: environment.secretIv,
        secretKey: environment.secretKey,
        apiUrlTokenCognito: environment.apiUrlTokenCognito,
        userCognito: environment.userCognito,
        passCognito: environment.passCognito,
        apiUrl: environment.apiUrl,
        dreamCodeApiUrl: environment.lambdaApiUrl,
        prodUrl: environment.environmenturl,
        apiLambdasUrl: environment.lambdaApiUrl,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LambdaSecurityService,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
